import { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';

import { Channels } from '@bandido/shared/constants';
import { LAYOUT_SIZES } from '@frontend/features/common/constants';
import { useScrollHandling } from '@frontend/features/common/hooks/useScrollHandling';
import { CaseHistoryResponseDto } from '@bandido/shared/dtos';
import { useGetMyUserData } from '@frontend/features/auth/queries';
import { useIntersectionObserver } from '@frontend/features/common/hooks/useIntersectionObserver';

import { useGetCaseHistory } from '../../queries';
import { useOnResize } from './hooks/useOnResize';
import { useMessageGroups } from './helpers';
import { CaseContentHistoryDesktop } from './CaseContentHistoryDesktop';
import { CaseContentHistoryMobile } from './CaseContentHistoryMobile';

interface CaseContentHistoryProps {
  channel: Channels | null;
  isMobile?: boolean;
}

export const CaseContentHistory = ({ channel, isMobile = false }: CaseContentHistoryProps) => {
  const { id } = useParams();
  const scrollRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef(null);

  const { data: { data: user } = {} } = useGetMyUserData();
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    hasNextPage = false,
    isError,
    refetch,
  } = useGetCaseHistory(Number(id), {
    channel,
  });

  const { shouldAutoScroll, scrollDirection, handleScroll } = useScrollHandling();

  const scrollInstance = useMemo(() => scrollRef.current?.osInstance()?.elements()?.viewport, [scrollRef.current]);

  useIntersectionObserver({
    targetRef: topRef,
    onIntersect: () => {
      fetchNextPage();
      scrollInstance?.scrollTo({ top: 250 });
    },
    enabled: !isMobile && !isFetchingNextPage && scrollDirection === 'up' && hasNextPage,
  });

  useOnResize(contentRef, () => {
    if (!scrollInstance || scrollDirection === 'up' || !shouldAutoScroll) return;
    const maxScrollHeight = scrollInstance.scrollHeight - scrollInstance.clientHeight;
    scrollInstance.scrollTop = maxScrollHeight;
  });

  const isCurrentUser = useCallback(
    (history: CaseHistoryResponseDto) => user?.clientId === history.authorId,
    [user?.clientId],
  );

  const contentHeight = `calc(100% - (${LAYOUT_SIZES.searchBar + LAYOUT_SIZES.integrationsBar}px))`;
  const allMessages = data?.pages?.map((page) => page.data.items).flat();
  const groupedMessages = useMessageGroups(allMessages);

  if (isMobile) {
    return (
      <CaseContentHistoryMobile
        groupedMessages={groupedMessages}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isError={isError}
        onLoadOlder={() => fetchNextPage()}
        onRetry={() => { void refetch(); }}
        isCurrentUser={isCurrentUser}
      />
    );
  }

  return (
    <CaseContentHistoryDesktop
      groupedMessages={groupedMessages}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
      scrollRef={scrollRef}
      topRef={topRef}
      contentRef={contentRef}
      handleScroll={handleScroll}
      isCurrentUser={isCurrentUser}
      contentHeight={contentHeight}
    />
  );
};
